import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { getAllExamStatistics } from '../../services/examAPIService';
import ExamCard from './ExamCard';
import StandardLayout from '../../components/layout/StandardLayout';
import TrialVolumeProgress from '../../components/common/TrialVolumeProgress';
import { AuthContext } from '../../context/AuthContext';

function ExamOverview() {
    const { hasSubscription } = useContext(AuthContext);
    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchExams = async () => {
            try {
                const data = await getAllExamStatistics();
                setExams(data);
                setLoading(false);
            } catch (err) {
                setError('Failed to load exam data');
                setLoading(false);
            }
        };

        fetchExams();
    }, []);

    if (loading) {
        return (
            <StandardLayout>
                <div className="min-h-screen flex items-center justify-center bg-base-100">
                    <div className="flex flex-col items-center gap-4">
                        <span className="loading loading-spinner loading-lg text-primary"></span>
                        <p className="text-base-content/70 animate-pulse">Lade Prüfungsbögen...</p>
                    </div>
                </div>
            </StandardLayout>
        );
    }

    if (error) {
        return (
            <StandardLayout>
                <div className="min-h-screen flex items-center justify-center bg-base-100 p-4">
                    <div className="alert alert-error max-w-md shadow-lg">
                        <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                            <h3 className="font-bold">Fehler beim Laden</h3>
                            <p className="text-sm">{error}</p>
                        </div>
                    </div>
                </div>
            </StandardLayout>
        );
    }

    const handleStartQuiz = (subcategoryId) => {
        navigate(`/pruefungsboegen/quiz/${subcategoryId}`);
    };

    const handleShowQuestions = (subcategoryId) => {
        navigate(`/exam-questions/subcategory/${subcategoryId}`);
    };

    return (
        <StandardLayout>
            <div className="min-h-screen bg-base-100">
                {/* Hero Section */}
                <div>
                    <div className="container mx-auto px-4 py-6 max-w-5xl">
                        <div className="text-center">
                            <div className="inline-flex p-4 rounded-full bg-primary/10 ring-1 ring-primary/20 mb-6">
                                <DocumentTextIcon className="w-8 h-8 text-primary" />
                            </div>
                            <h1 className="text-2xl sm:text-3xl font-bold mb-3 text-base-content">
                                Prüfungsübersicht
                            </h1>
                            <p className="text-base-content/70 max-w-2xl mx-auto">
                                Übe mit original Prüfungsfragen und bereite dich optimal auf deine Prüfung vor
                            </p>
                        </div>
                    </div>
                </div>

                {/* Content Section */}
                <div className="container mx-auto py-8 max-w-5xl">
                    <div className="space-y-6">
                        {exams.map((exam) => (
                            <ExamCard 
                                key={exam.id} 
                                exam={exam}
                                onStartQuiz={handleStartQuiz}
                                onShowQuestions={handleShowQuestions}
                            />
                        ))}
                    </div>
                </div>

                {!hasSubscription && <TrialVolumeProgress />}
            </div>
        </StandardLayout>
    );
}

export default ExamOverview; 